import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { Slider } from '@/components/ui/slider';
import { 
  Play, 
  Pause, 
  SkipBack, 
  SkipForward, 
  Volume2, 
  VolumeX,
  Repeat,
  Repeat1,
  Music
} from 'lucide-react';
import { cn } from '@/lib/utils';

const MobilePlayer = React.memo(function MobilePlayer({
  currentSong,
  isPlaying = false,
  currentTime = 0,
  duration = 0,
  volume = 0.7,
  isLooping = false,
  isPlaylistLooping = false,
  handlePlayPause = () => {}, 
  onSeek = () => {},
  onVolumeChange = () => {},
  onToggleLoop = () => {},
  onTogglePlaylistLoop = () => {},
  onNext = () => {},
  onPrevious = () => {},
  orientation = 'portrait'
}) {
  const [isMuted, setIsMuted] = useState(false);
  const [previousVolume, setPreviousVolume] = useState(volume);
  const [localVolume, setLocalVolume] = useState(volume * 100);

  useEffect(() => {
    if (!isMuted) {
      setPreviousVolume(volume);
    }
  }, [volume, isMuted]);

  useEffect(() => {
    setLocalVolume(volume * 100);
  }, [volume]);

  const formatTime = (time) => {
    if (time === null || typeof time === 'undefined' || isNaN(time)) return '0:00';
    const minutes = Math.floor(time / 60);
    const seconds = Math.floor(time % 60);
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  };

  const handleVolumeToggle = () => {
    if (isMuted || volume === 0) {
      onVolumeChange(previousVolume > 0 ? previousVolume : 0.7);
      setIsMuted(false);
    } else {
      setPreviousVolume(volume); 
      onVolumeChange(0);
      setIsMuted(true);
    }
  };

  const handleVolumeSliderChange = (value) => { 
    const newVol = value[0] / 100;
    setLocalVolume(value[0]);
    onVolumeChange(newVol);
    setIsMuted(newVol === 0);
  };

  const progress = duration > 0 ? (currentTime / duration) * 100 : 0;
  const isPortrait = orientation === 'portrait';

  return (
    <motion.div 
      initial={{ y: 100, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.3 }}
      className={cn(
        "bg-black/40 backdrop-blur-xl border-t border-white/10",
        !isPortrait && "max-h-[180px]"
      )}
    >
      {/* Song Info & Progress Bar */}
      <div className={cn(
        "px-4",
        isPortrait ? "pt-3 pb-2" : "pt-2 pb-1"
      )}>
        {/* Song Title */}
        <div className={cn("mb-2", !isPortrait && "mb-1")}>
          <div className="flex items-center gap-2">
            {!currentSong ? (
              <Music className="h-4 w-4 text-white/40" />
            ) : null}
            <h3 className={cn(
              "font-semibold text-white truncate flex-1",
              isPortrait ? "text-sm" : "text-xs"
            )}>
              {currentSong?.name || 'No song selected'}
            </h3>
          </div>
        </div>

        {/* Progress Bar */}
        <div className="space-y-1">
          <Slider
            value={[currentTime || 0]}
            max={duration || 100}
            step={0.1}
            onValueChange={(value) => onSeek(value[0])}
            className="w-full cursor-pointer"
          />
          <div className="flex justify-between text-[10px] text-white/50">
            <span>{formatTime(currentTime)}</span>
            <span>{formatTime(duration)}</span>
          </div>
        </div>
      </div>

      {/* Main Controls */}
      <div className={cn(
        "px-4 flex items-center justify-between",
        isPortrait ? "gap-2 pb-3" : "gap-3 pb-2"
      )}>
        {/* Loop Button */}
        <Button
          variant="ghost"
          size="icon"
          className={cn(
            "shrink-0",
            isPortrait ? "h-9 w-9" : "h-8 w-8",
            (isLooping || isPlaylistLooping) ? "text-blue-400" : "text-white/60"
          )}
          onClick={onToggleLoop}
        >
          {isLooping ? (
            <Repeat1 className="h-4 w-4" />
          ) : (
            <Repeat className="h-4 w-4" />
          )}
        </Button>

        {/* Playback Controls */}
        <div className={cn(
          "flex items-center justify-center flex-1",
          isPortrait ? "gap-2" : "gap-1"
        )}>
          <Button
            variant="ghost"
            size="icon"
            className={cn(
              "text-white",
              isPortrait ? "h-11 w-11" : "h-9 w-9"
            )}
            onClick={onPrevious}
          >
            <SkipBack className={cn(isPortrait ? "h-5 w-5" : "h-4 w-4")} fill="currentColor" />
          </Button>

          <Button
            variant="default"
            size="icon"
            className={cn(
              "rounded-full bg-gradient-to-br from-blue-500 to-indigo-600 hover:from-blue-600 hover:to-indigo-700 shadow-lg",
              isPortrait ? "h-14 w-14" : "h-12 w-12"
            )}
            onClick={handlePlayPause}
          >
            {isPlaying ? (
              <Pause className={cn(isPortrait ? "h-6 w-6" : "h-5 w-5")} fill="currentColor" />
            ) : (
              <Play className={cn(isPortrait ? "h-6 w-6 ml-0.5" : "h-5 w-5 ml-0.5")} fill="currentColor" />
            )}
          </Button>

          <Button
            variant="ghost"
            size="icon"
            className={cn(
              "text-white",
              isPortrait ? "h-11 w-11" : "h-9 w-9"
            )}
            onClick={onNext}
          >
            <SkipForward className={cn(isPortrait ? "h-5 w-5" : "h-4 w-4")} fill="currentColor" />
          </Button>
        </div>

        {/* Volume Control */}
        <div className={cn(
          "flex items-center shrink-0",
          isPortrait ? "gap-2" : "gap-1"
        )}>
          <Button
            variant="ghost"
            size="icon"
            className={cn(
              "text-white/80",
              isPortrait ? "h-9 w-9" : "h-8 w-8"
            )}
            onClick={handleVolumeToggle}
          >
            {isMuted || volume === 0 ? (
              <VolumeX className="h-4 w-4" />
            ) : (
              <Volume2 className="h-4 w-4" />
            )}
          </Button>
          
          {!isPortrait && (
            <div className="w-20">
              <Slider
                value={[localVolume]}
                max={100}
                step={1}
                onValueChange={handleVolumeSliderChange}
                className="cursor-pointer"
              />
            </div>
          )}
        </div>
      </div>
    </motion.div>
  );
});

export default MobilePlayer;
